import React, { ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { ActivityData, CourseData } from 'course-types';
import UtilityService from '@/services/util/utility-service';

interface BreadCrumb {
  displayName: string;
  pushPath: string;
}

interface BreadcrumbNavProps {
  pathName: string;
  course: CourseData;
  activity: ActivityData;
  darkMode: boolean;
  userRole: string;
}

export function BreadcrumbNav({
  pathName,
  course,
  activity,
  darkMode,
  userRole,
}: BreadcrumbNavProps): ReactNode {
  const router = useRouter();

  if (userRole === '') return <></>;

  const normalizedPath = pathName.replace(/^\/+|\/+$/g, '');
  const slices = normalizedPath ? normalizedPath.split('/') : [];
  const roleDisplayName =
    userRole === 'student' ? 'Student' : userRole === 'instructor' ? 'Instructor' : 'Admin';
  const rolePathRoot =
    userRole === 'student' ? 'student' : userRole === 'instructor' ? 'instructor' : 'admin';
  const courseOverviewPath = userRole === 'student' ? 'student/course' : 'admin/courses';
  const relativeSlices =
    slices.length > 0 && (slices[0] === 'student' || slices[0] === 'admin')
      ? slices.slice(1)
      : slices;

  const generatedCrumbs = relativeSlices.flatMap((slice, i) => {
    const crumbs: BreadCrumb[] = [];

    if (slice === 'lesson') {
      crumbs.push({ displayName: 'Course Overview', pushPath: courseOverviewPath });
      crumbs.push({
        displayName: course.name || 'Course',
        pushPath: [rolePathRoot, ...relativeSlices.slice(0, i + 1)].join('/'),
      });
      return crumbs;
    }

    if (slice === 'edit') {
      crumbs.push({
        displayName: activity.name,
        pushPath:
          [rolePathRoot, ...relativeSlices.slice(0, i + 1)].join('/') + `/?id=${activity.id}`,
      });
      return crumbs;
    }

    const crumb: BreadCrumb = {
      displayName: UtilityService.cleanString(slice),
      pushPath: [rolePathRoot, ...relativeSlices.slice(0, i + 1)].join('/'),
    };

    return [crumb];
  });

  const breadCrumbs: BreadCrumb[] = [
    { displayName: roleDisplayName, pushPath: rolePathRoot },
    ...generatedCrumbs,
  ];

  return (
    <>
      {breadCrumbs.map((crumb, i) => (
        <div key={i} className='flex flex-row'>
          <li
            onClick={() => {
              const mappedPushPath =
                userRole === 'instructor' && crumb.pushPath.startsWith('instructor')
                  ? `admin${crumb.pushPath.slice('instructor'.length)}`
                  : crumb.pushPath;
              router.push(`/${mappedPushPath}`);
            }}
            className={`cursor-pointer ${i === breadCrumbs.length - 1 ? (darkMode ? 'text-primary_highlight' : 'text-primary') : 'text-text_gray_600'} hover:underline font-normal`}
          >
            {crumb.displayName}
          </li>

          <p className={`${i === breadCrumbs.length - 1 ? 'hidden' : 'mr-2'} text-text_gray_600`}>
            {' '}
            /{' '}
          </p>
        </div>
      ))}
    </>
  );
}
